import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Table, Tag, Button, Space, Modal, message, Form, Input, Select } from 'antd';
import { deleteUser, getUsers, updateUser, type AdminUser } from '../services/api';
import './UserManage.css';

const formatTime = (timestamp: number) => new Date(timestamp * 1000).toLocaleString('zh-CN');

const UserManage: React.FC = () => {
  const pageRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [tableScrollY, setTableScrollY] = useState(360);
  const [form] = Form.useForm();

  const updateTableScrollY = useCallback(() => {
    if (!pageRef.current || !headerRef.current || !tableWrapRef.current) {
      return;
    }

    const containerHeight = pageRef.current.getBoundingClientRect().height;
    const headerHeight = headerRef.current.getBoundingClientRect().height;
    const headerGap = 8;
    const tableWrapTop = tableWrapRef.current.getBoundingClientRect().top;
    const tableWrapHeight = Math.max(0, containerHeight - headerHeight - headerGap);
    const tableWrapBottom = tableWrapTop + tableWrapHeight;

    const tableBodyEl = tableWrapRef.current.querySelector('.ant-table-body') as HTMLElement | null;
    const paginationEl = tableWrapRef.current.querySelector('.ant-table-pagination') as HTMLElement | null;
    if (!tableBodyEl) {
      return;
    }

    const bodyTop = tableBodyEl.getBoundingClientRect().top;
    const paginationRect = paginationEl?.getBoundingClientRect();
    const paginationTop = paginationRect?.top ?? tableWrapBottom - 64;
    const paginationHeight = paginationRect?.height ?? 56;
    const bodyReserve = 12;
    const bottomSafe = 8;
    const maxBodyBottom = Math.min(paginationTop, tableWrapBottom - paginationHeight - bottomSafe);
    const next = Math.max(120, Math.floor(maxBodyBottom - bodyTop - bodyReserve));
    setTableScrollY(prev => (prev === next ? prev : next));
  }, []);

  const loadData = useCallback(async (currentPage = page, currentPageSize = pageSize) => {
    setLoading(true);
    try {
      const res = await getUsers(currentPage, currentPageSize);
      if (res.code !== 0) {
        message.error(res.message || '加载用户失败');
        return;
      }
      setData(res.data.list);
      setTotal(res.data.total);
    } catch (error) {
      message.error('加载用户失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useLayoutEffect(() => {
    updateTableScrollY();
    const id = window.requestAnimationFrame(updateTableScrollY);
    return () => window.cancelAnimationFrame(id);
  }, [updateTableScrollY, pageSize, total, data.length, loading]);

  useEffect(() => {
    updateTableScrollY();
    window.addEventListener('resize', updateTableScrollY);

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        updateTableScrollY();
      });
      if (pageRef.current) {
        observer.observe(pageRef.current);
      }
      if (headerRef.current) {
        observer.observe(headerRef.current);
      }
      if (tableWrapRef.current) {
        observer.observe(tableWrapRef.current);
      }
    }

    return () => {
      window.removeEventListener('resize', updateTableScrollY);
      observer?.disconnect();
    };
  }, [updateTableScrollY]);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '昵称', dataIndex: 'nickname', key: 'nickname' },
    { title: '手机号', dataIndex: 'phone', key: 'phone', render: (phone: string) => phone || '-' },
    { title: '状态', dataIndex: 'status', key: 'status', render: (status: number) => (
      <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '正常' : '禁用'}</Tag>
    )},
    { title: '注册时间', dataIndex: 'created_at', key: 'created_at', render: (createdAt: number) => formatTime(createdAt) },
    { title: '操作', key: 'action', width: 180, render: (_: unknown, record: AdminUser) => (
      <Space>
        <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
        <Button type="link" danger size="small" onClick={() => handleDelete(record.id)}>删除</Button>
      </Space>
    )},
  ];

  const handleEdit = (record: AdminUser) => {
    setCurrentUser(record);
    form.setFieldsValue(record);
    setEditVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除该用户吗？',
      onOk: async () => {
        const res = await deleteUser(id);
        if (res.code !== 0) {
          message.error(res.message || '删除失败');
          return;
        }

        message.success('删除成功');
        const nextPage = data.length === 1 && page > 1 ? page - 1 : page;
        if (nextPage !== page) {
          setPage(nextPage);
          return;
        }
        void loadData(nextPage, pageSize);
      },
    });
  };

  const handleSubmit = async () => {
    if (!currentUser) {
      return;
    }

    try {
      const values = await form.validateFields();
      const res = await updateUser(currentUser.id, {
        nickname: values.nickname.trim(),
        phone: values.phone?.trim() || '',
        status: values.status,
      });
      if (res.code !== 0) {
        message.error(res.message || '更新失败');
        return;
      }
      message.success('更新成功');
      setEditVisible(false);
      void loadData();
    } catch (error) {
      message.error('更新失败');
    }
  };

  return (
    <div className="user-manage-page" ref={pageRef}>
      <div className="user-manage-header" ref={headerRef}>
        <h1 className="user-manage-title">用户管理</h1>
      </div>
      <div className="user-manage-table-wrap" ref={tableWrapRef}>
        <Table
          className="user-manage-table"
          dataSource={data}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ y: tableScrollY }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            },
          }}
        />
      </div>
      
      <Modal title="编辑用户" open={editVisible} onOk={handleSubmit} onCancel={() => setEditVisible(false)}>
        <Form form={form} layout="vertical">
          <Form.Item name="nickname" label="昵称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="手机号" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="status" label="状态" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 1, label: '正常' },
                { value: 2, label: '禁用' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManage;
